import {
  ContinuityStatusResponse,
  CreditsSummaryResponse,
  DocumentationData,
  FinancialBlockageResponse,
  PaymentData,
  StudentFileData,
  TuitionValueData,
} from '../../types/tuitionContinuityV2';
import { TuitionForbiddenEvent } from '../../utils/tuitionForbiddenEvent';
import { CONTINUITY_V2 } from '../endpoints';
import request, { RequestOptionsType } from '../request';

const tcv2Request = async <T>(
  url: string,
  options: RequestOptionsType = {},
) => {
  const response = await request<T>(url, options);
  const { error } = response;
  if (error && error.data.statusCode === 403) {
    window.dispatchEvent(new TuitionForbiddenEvent());
  }
  return response;
};

export const getStatus = (studyPlanEnrollmentId: number, periodId: number) =>
  tcv2Request<ContinuityStatusResponse>(CONTINUITY_V2.STATUS, {
    method: 'get',
    params: { studyPlanEnrollmentId, periodId },
  });

export const getCreditsSummary = (
  studyPlanEnrollmentId: number,
  periodId: number,
) =>
  tcv2Request<CreditsSummaryResponse>(CONTINUITY_V2.CREDITS_SUMMARY, {
    method: 'get',
    params: { studyPlanEnrollmentId, periodId },
  });

export const checkFinancialBlockage = (studentId: number) =>
  tcv2Request<FinancialBlockageResponse>(CONTINUITY_V2.FINANCIAL_BLOCKAGE, {
    method: 'get',
    params: { studentId },
  });

export const startProcess = (
  studyPlanEnrollmentId: number,
  academicCalendarId: number,
) =>
  tcv2Request<{ processId: number; currentStep: string }>(CONTINUITY_V2.START, {
    method: 'post',
    data: { studyPlanEnrollmentId, academicCalendarId },
  });

export const getStudentFile = (processId: number) =>
  tcv2Request<StudentFileData>(CONTINUITY_V2.STUDENT_FILE(processId), {
    method: 'get',
  });

export const updateStudentFile = (
  processId: number,
  data: Partial<StudentFileData>,
) =>
  tcv2Request(CONTINUITY_V2.STUDENT_FILE(processId), {
    method: 'put',
    data,
  });

export const getTuitionValue = (processId: number) =>
  tcv2Request<TuitionValueData>(CONTINUITY_V2.TUITION_VALUE(processId), {
    method: 'get',
  });

export const requestSimpleBenefit = (
  processId: number,
  data: { benefitId: number; fileId: number },
) =>
  tcv2Request(CONTINUITY_V2.BENEFITS_SIMPLE(processId), {
    method: 'post',
    data,
  });

export const requestSubsidyBenefit = (
  processId: number,
  data: { benefitId: number; fileId: number; subsidyCode?: string },
) =>
  tcv2Request(CONTINUITY_V2.BENEFITS_SUBSIDY(processId), {
    method: 'post',
    data,
  });

export const removeBenefit = (processId: number, benefitId: number) =>
  tcv2Request(CONTINUITY_V2.BENEFITS_REMOVE(processId, benefitId), {
    method: 'delete',
  });

export const getDocumentation = (processId: number) =>
  tcv2Request<DocumentationData>(CONTINUITY_V2.DOCUMENTATION(processId), {
    method: 'get',
  });

export const uploadDocument = (processId: number, data: FormData) =>
  tcv2Request(CONTINUITY_V2.DOCUMENTATION_UPLOAD(processId), {
    method: 'post',
    data,
    formData: true,
  });

export const getPaymentDetail = (processId: number) =>
  tcv2Request<PaymentData>(CONTINUITY_V2.PAYMENT(processId), {
    method: 'get',
  });

export const createPaymentOrder = (processId: number) =>
  tcv2Request<{ returnUrl: string }>(CONTINUITY_V2.PAYMENT_CREATE(processId), {
    method: 'post',
  });

export const getPaymentStatus = (processId: number) =>
  tcv2Request<{ status: string }>(CONTINUITY_V2.PAYMENT_STATUS(processId), {
    method: 'get',
  });

export const getPaymentVoucher = (processId: number) =>
  tcv2Request<{ url: string }>(CONTINUITY_V2.PAYMENT_VOUCHER(processId), {
    method: 'get',
  });

export const getRedirectToken = () =>
  tcv2Request<{ attemptId: string }>(CONTINUITY_V2.REDIRECT_TOKEN, {
    method: 'post',
  });

export const completeStep = (processId: number, step: string) =>
  tcv2Request<{ nextStep: string }>(
    CONTINUITY_V2.COMPLETE_STEP(processId, step),
    { method: 'post' },
  );
